import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { ChevronDownIcon } from '../Icons/Navigational/ChevronDown'
import styles from './Dropdown.module.scss'

export const Dropdown: FunctionComponent<DropdownProps> = ({
  id,
  name,
  callback,
  defaultValue,
  options,
  placeholderOption,
  disabled,
  wrapperClassName,
  className,
  selectRef,
  variant = 'short',
  ...rest
}) => {
  const optionChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.currentTarget
    typeof callback === 'function' && callback(value)
  }

  return (
    <div className={classnames(styles.container, wrapperClassName)}>
      <select
        className={classnames(
          styles.dropdown,
          {
            [styles.dropdownShort]: variant === 'short',
            [styles.dropdownRegular]: variant === 'regular',
          },
          className
        )}
        data-testid="dropdown-select"
        defaultValue={placeholderOption?.value || defaultValue}
        disabled={disabled}
        id={id}
        name={name}
        onChange={optionChangeHandler}
        ref={selectRef}
        {...rest}
      >
        {placeholderOption && (
          <option disabled hidden value={placeholderOption.value}>
            {placeholderOption.label}
          </option>
        )}
        {options.map(({ label, value }, index) => (
          <option key={`${value}-${index}`} value={value}>
            {label}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        className={classnames(
          styles.chevronIcon,
          disabled && styles.chevronIconDisabled,
          chevronIconStylesMapper[variant]
        )}
      />
    </div>
  )
}

///////// IMPLEMENTATION /////////

const chevronIconStylesMapper = {
  short: styles.chevronIconShort,
  regular: styles.chevronIconRegular,
}

export type DropdownProps = {
  id?: string
  placeholderOption?: Option
  name?: string
  disabled?: boolean
  options: Array<Option>
  defaultValue?: string
  callback: (value: string) => void
  /**
   * Class name to be added to the div surrounding the select element
   */
  wrapperClassName?: string
  /**
   * Class name to be added to the select element
   */
  className?: string
  /**
   * Style variant for the dropdown. 'short' is the default variant for backwards compatibility reasons.
   */
  variant?: 'short' | 'regular'
  selectRef?: React.RefObject<HTMLSelectElement>
} & ComponentPropsWithoutRef<'select'>

export type Option = { label: string; value: string | number }
